import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import BackupIcon from '@mui/icons-material/Backup';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestoreIcon from '@mui/icons-material/Restore';
import UpdateIcon from '@mui/icons-material/Update';
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  CardActions,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  TableBody,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import { SelectChangeEvent } from '@mui/material/Select';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import React, { useState, useEffect } from 'react';

import NotificationSubscriber from '../../../components/notifications/NotificationSubscriber';
import { useAuth } from '../../../contexts/Auth';
import databaseBackupService, { DatabaseBackup, BackupResult } from '../../../services/databaseBackupService';
import databaseQueryService from '../../../services/databaseQueryService';
import settingsService from '../../../services/settingsService';
import { BackupJobDto, BackupJobStatus } from '../../../types/backupJob';
import { DatabaseBackupSettings, BackupJob } from '../../../types/settings';
// Dönüştürme fonksiyonları bileşen içinde tanımlandı

// API'den gelen Veritabanı interface'i
interface Database {
  id: string;
  name: string;
  databaseName: string;
  companyName: string;
  serverName: string;
  serverPort: number;
  isActive: boolean;
}

const DatabaseBackupTab = (): JSX.Element => {
  const { apiUrl, isAuthenticated } = useAuth();
  const [databases, setDatabases] = useState<Database[]>([]);
  const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
  const [backups, setBackups] = useState<DatabaseBackup[]>([]);
  const [selectedBackup, setSelectedBackup] = useState<DatabaseBackup | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [backupLoading, setBackupLoading] = useState<boolean>(false);
  const [restoreLoading, setRestoreLoading] = useState<boolean>(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState<boolean>(false);
  const [settingsLoading, setSettingsLoading] = useState<boolean>(false);
  const [jobDialogOpen, setJobDialogOpen] = useState<boolean>(false);
  const [currentJob, setCurrentJob] = useState<BackupJobDto | null>(null);
  const [isEditingJob, setIsEditingJob] = useState<boolean>(false);
  const [deleteJobDialogOpen, setDeleteJobDialogOpen] = useState<boolean>(false);
  const [backupJobs, setBackupJobs] = useState<BackupJobDto[]>([]);
  const [jobsLoading, setJobsLoading] = useState<boolean>(false);
  const [backupSettings, setBackupSettings] = useState<DatabaseBackupSettings>({
    enabled: true,
    backupDirectory: '/backups',
    backupFrequency: 'daily',
    backupTime: '03:00',
    backupType: 'full',
    keepBackupsForDays: 30,
    compressBackups: true,
    databaseNames: [],
    backupJobs: []
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // BackupJobDto'yu BackupJob'a dönüştüren yardımcı fonksiyon
  const convertToBackupJob = (dto: BackupJobDto): BackupJob => {
    if (!dto) {
      // Varsayılan bir BackupJob nesnesi döndür
      return {
        id: '',
        name: '',
        databaseNames: [],
        backupType: 'full',
        schedule: {
          frequency: 'daily',
          time: '03:00',
          daysOfWeek: [1],
          dayOfMonth: 1
        },
        retention: {
          keepBackupsForDays: 30,
          compressBackups: true
        },
        destination: {
          directory: '/backups',
          useCompression: true
        },
        status: 'paused'
      };
    }

    // Saat ve dakika bilgisinden HH:MM formatında zaman oluştur
    const hour = typeof dto.hour === 'number' ? dto.hour.toString().padStart(2, '0') : '00';
    const minute = typeof dto.minute === 'number' ? dto.minute.toString().padStart(2, '0') : '00';
    const timeStr = `${hour}:${minute}`;
    
    // BackupType'ı doğru formata dönüştür
    let backupType: 'full' | 'differential' | 'incremental' = 'full';
    if (dto.backupType === 'differential' || dto.backupType === 'incremental') {
      backupType = dto.backupType;
    }
    
    return {
      id: dto.id?.toString() || '',
      name: dto.databaseName || 'Yeni Yedekleme Görevi',
      databaseNames: [dto.databaseId || ''],
      backupType: backupType,
      schedule: {
        frequency: (dto.frequency as 'daily' | 'weekly' | 'monthly' | 'custom') || 'daily',
        time: timeStr,
        daysOfWeek: [1], // Varsayılan olarak pazartesi
        dayOfMonth: 1 // Varsayılan olarak ayın ilk günü
      },
      retention: {
        keepBackupsForDays: 30,
        compressBackups: true
      },
      destination: {
        directory: '/backups',
        useCompression: true
      },
      lastRunDate: dto.lastRunTime,
      errorMessage: dto.lastRunStatus === 'FAILED' ? 'Yedekleme başarısız oldu' : undefined,
      status: dto.isEnabled ? 'active' : 'paused'
    };
  };
  
  // BackupJob'u BackupJobDto'ya dönüştüren yardımcı fonksiyon
  const convertToBackupJobDto = (job: BackupJob | null): BackupJobDto => {
    if (!job) {
      // Varsayılan bir BackupJobDto nesnesi döndür
      return {
        databaseId: '',
        backupType: 'full',
        frequency: 'daily',
        hour: 3,
        minute: 0,
        isEnabled: false
      };
    }

    // HH:MM formatındaki zamanı saat ve dakika olarak ayır
    const [hourStr, minuteStr] = (job.schedule?.time || '03:00').split(':');
    const hour = parseInt(hourStr, 10) || 0;
    const minute = parseInt(minuteStr, 10) || 0;
    
    // Veritabanı adlarını bul
    const databaseNames = job.databaseNames || [];
    const databaseId = databaseNames.length > 0 ? databaseNames[0] : '';
    
    return {
      id: job.id ? parseInt(job.id, 10) || undefined : undefined,
      databaseId: databaseId,
      databaseName: job.name || '',
      backupType: job.backupType || 'full',
      frequency: job.schedule?.frequency || 'daily',
      hour: hour,
      minute: minute,
      isEnabled: job.status === 'active',
      lastRunTime: job.lastRunDate,
      lastRunStatus: job.errorMessage ? 'FAILED' : job.lastRunDate ? 'COMPLETED' : 'PENDING'
    };
  };
  
  // BackupJobDto dizisini BackupJob dizisine dönüştüren yardımcı fonksiyon
  const convertDtosToBackupJobs = (dtos: BackupJobDto[]): BackupJob[] => {
    return dtos.map(dto => convertToBackupJob(dto));
  };

  // Zamanlanmış yedekleme işlerini yükle
  const fetchBackupJobs = async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      setJobsLoading(true);
      const response = await databaseBackupService.getScheduledBackupJobs();
      
      if (response && response.success && Array.isArray(response.data)) {
        // Ensure response.data is an array before setting state
        setBackupJobs(response.data || []);
        
        // Aynı zamanda backupSettings içindeki backupJobs dizisini de güncelle
        // Bu, geriye uyumluluk için gerekli
        const backupJobsConverted = response.data.map(dto => convertToBackupJob(dto));
        setBackupSettings(prev => ({
          ...prev,
          backupJobs: Array.isArray(backupJobsConverted) ? backupJobsConverted : []
        }));
      } else {
        // Set empty arrays as defaults when API fails
        setBackupJobs([]);
        setBackupSettings(prev => ({
          ...prev,
          backupJobs: []
        }));
        
        const errorMessage = typeof response === 'object' && response !== null && 'message' in response ? response.message : 'Zamanlanmış yedekleme işleri yüklenirken hata oluştu';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Zamanlanmış yedekleme işleri yüklenirken hata oluştu:', error);
      // Set empty arrays as defaults when exception occurs
      setBackupJobs([]);
      setBackupSettings(prev => ({
        ...prev,
        backupJobs: []
      }));
      
      setSnackbar({
        open: true,
        message: 'Zamanlanmış yedekleme işleri yüklenirken hata oluştu',
        severity: 'error'
      });
    } finally {
      setJobsLoading(false);
    }
  };

  // Veritabanlarını yükle
  const fetchDatabases = async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      setLoading(true);
      const response = await databaseQueryService.listDatabases();
      
      if (response.success) {
        setDatabases(response.data);
        if (response.data.length > 0 && selectedDatabases.length === 0) {
          setSelectedDatabases([response.data[0].id]);
        }
      } else {
        const errorMessage = typeof response === 'object' && response !== null && 'message' in response ? response.message : 'Veritabanları yüklenirken hata oluştu';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Veritabanları yüklenirken hata oluştu:', error);
      setSnackbar({
        open: true,
        message: 'Veritabanları yüklenirken hata oluştu',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Yedekleme ayarlarını yükle
  const fetchBackupSettings = async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      setSettingsLoading(true);
      const settings = await settingsService.getDatabaseBackupSettings();
      
      // Ensure settings has all required properties with default values if missing
      const validatedSettings = {
        enabled: settings?.enabled ?? true,
        backupDirectory: settings?.backupDirectory ?? '/backups',
        backupFrequency: settings?.backupFrequency ?? 'daily',
        backupTime: settings?.backupTime ?? '03:00',
        backupType: settings?.backupType ?? 'full',
        keepBackupsForDays: settings?.keepBackupsForDays ?? 30,
        compressBackups: settings?.compressBackups ?? true,
        databaseNames: Array.isArray(settings?.databaseNames) ? settings.databaseNames : [],
        backupJobs: Array.isArray(settings?.backupJobs) ? settings.backupJobs : []
      };
      
      setBackupSettings(validatedSettings);
    } catch (error) {
      console.error('Yedekleme ayarları yüklenirken hata oluştu:', error);
      setSnackbar({
        open: true,
        message: 'Yedekleme ayarları yüklenirken hata oluştu',
        severity: 'error'
      });
      
      // Even on error, ensure backupSettings has valid default values
      setBackupSettings(prev => ({
        ...prev,
        backupJobs: prev.backupJobs || []
      }));
    } finally {
      setSettingsLoading(false);
    }
  };

  // İlk yükleme için veritabanlarını ve ayarları getir
  useEffect(() => {
    if (isAuthenticated) {
      fetchDatabases();
      fetchBackupSettings();
      fetchBackupJobs();
    }
  }, [isAuthenticated]);

  // Seçili veritabanı değiştiğinde yedekleri yükle
  // Yedekleri yükle
  const fetchBackups = async () => {
    if (!isAuthenticated || selectedDatabases.length === 0) {
      setBackups([]);
      return;
    }

    try {
      setLoading(true);
      const primaryDbId = selectedDatabases[0];
      const response = await databaseBackupService.getBackups(primaryDbId);
      
      if (response.success) {
        setBackups(response.data);
      } else {
        const errorMessage = typeof response === 'object' && response !== null && 'message' in response ? response.message : 'Yedekler yüklenirken hata oluştu';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
        setBackups([]);
      }
    } catch (error) {
      console.error('Yedekler yüklenirken hata oluştu:', error);
      setSnackbar({
        open: true,
        message: `Yedekler yüklenirken hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`,
        severity: 'error'
      });
      setBackups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && selectedDatabases.length > 0) {
      fetchBackups();
    }
  }, [selectedDatabases, isAuthenticated]);

  // Veritabanı değişikliği
  const handleDatabaseChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedDatabases(typeof value === 'string' ? [value] : value);
  };

  // Tam yedek oluştur
  const handleCreateFullBackup = async () => {
    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Bu işlem için oturum açmanız gerekiyor',
        severity: 'error'
      });
      return;
    }
    
    if (selectedDatabases.length === 0) {
      setSnackbar({
        open: true,
        message: 'Lütfen en az bir veritabanı seçin',
        severity: 'warning'
      });
      return;
    }
    
    setBackupLoading(true);
    try {
      // Seçilen her veritabanı için tam yedek işlemi başlat
      const backupPromises = selectedDatabases.map(dbId => {
        // GUID kontrolü yap
        if (!dbId || dbId === 'undefined' || dbId === 'null') {
          console.error('Geçersiz veritabanı ID:', dbId);
          return Promise.reject(new Error(`Geçersiz veritabanı ID: ${dbId}`));
        }
        
        return databaseBackupService.createFullBackup(dbId);
      });
      
      const results = await Promise.all(backupPromises);
      
      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;
      
      if (failCount === 0) {
        setSnackbar({
          open: true,
          message: `${successCount} veritabanı için tam yedek başarıyla oluşturuldu`,
          severity: 'success'
        });
      } else if (successCount === 0) {
        setSnackbar({
          open: true,
          message: `Tüm yedekleme işlemleri başarısız oldu`,
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: `${successCount} veritabanı için tam yedek oluşturuldu, ${failCount} veritabanı için başarısız oldu`,
          severity: 'warning'
        });
      }
      
      // Yedekleri yeniden yükle
      await fetchBackups();
    } catch (error: unknown) {
      console.error('Yedekleme hatası:', error);
      setSnackbar({
        open: true,
        message: `Yedekleme sırasında bir hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`,
        severity: 'error'
      });
    } finally {
      setBackupLoading(false);
    }
  };

  // Fark yedeği oluştur
  const handleCreateDifferentialBackup = async () => {
    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Bu işlem için oturum açmanız gerekiyor',
        severity: 'error'
      });
      return;
    }
    
    if (selectedDatabases.length === 0) {
      setSnackbar({
        open: true,
        message: 'Lütfen en az bir veritabanı seçin',
        severity: 'warning'
      });
      return;
    }
    
    setBackupLoading(true);
    try {
      // Seçilen her veritabanı için fark yedeği işlemi başlat
      const backupPromises = selectedDatabases.map(dbId => {
        // GUID kontrolü yap
        if (!dbId || dbId === 'undefined' || dbId === 'null') {
          console.error('Geçersiz veritabanı ID:', dbId);
          return Promise.reject(new Error(`Geçersiz veritabanı ID: ${dbId}`));
        }
        
        return databaseBackupService.createDifferentialBackup(dbId);
      });
      
      const responses = await Promise.all(backupPromises);
      
      // Başarılı ve başarısız yedeklemeleri say
      const successCount = responses.filter(r => r.success).length;
      const failCount = responses.length - successCount;
      
      setSnackbar({
        open: true,
        message: `${successCount} veritabanı için fark yedeği tamamlandı${failCount > 0 ? `, ${failCount} veritabanı için başarısız oldu` : ''}`,
        severity: successCount > 0 ? (failCount > 0 ? 'warning' : 'success') : 'error'
      });
      
      // Yedekleri yeniden yükle
      await fetchBackups();
    } catch (error) {
      console.error('Yedekleme hatası:', error);
      setSnackbar({
        open: true,
        message: `Fark yedeği oluşturulurken bir hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`,
        severity: 'error'
      });
    } finally {
      setBackupLoading(false);
    }
  };
  
  // Artan yedek oluştur
  const handleCreateIncrementalBackup = async () => {
    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Bu işlem için oturum açmanız gerekiyor',
        severity: 'error'
      });
      return;
    }
    
    if (selectedDatabases.length === 0) {
      setSnackbar({
        open: true,
        message: 'Lütfen en az bir veritabanı seçin',
        severity: 'warning'
      });
      return;
    }
    
    setBackupLoading(true);
    try {
      // Seçilen her veritabanı için artan yedek işlemi başlat
      // Not: Şu anda API'de createIncrementalBackup metodu olmadığı için createFullBackup kullanıyoruz
      // İleride API'ye incremental backup özelliği eklendiğinde burayı güncelleyin
      const backupPromises = selectedDatabases.map(dbId => {
        // GUID kontrolü yap
        if (!dbId || dbId === 'undefined' || dbId === 'null') {
          console.error('Geçersiz veritabanı ID:', dbId);
          return Promise.reject(new Error(`Geçersiz veritabanı ID: ${dbId}`));
        }
        
        return databaseBackupService.createFullBackup(dbId);
      });
      
      const responses = await Promise.all(backupPromises);
      
      // Başarılı ve başarısız yedeklemeleri say
      const successCount = responses.filter(r => r.success).length;
      const failCount = responses.length - successCount;
      
      setSnackbar({
        open: true,
        message: `${successCount} veritabanı için artan yedek tamamlandı${failCount > 0 ? `, ${failCount} veritabanı için başarısız oldu` : ''}`,
        severity: successCount > 0 ? (failCount > 0 ? 'warning' : 'success') : 'error'
      });
      
      // Yedekleri yeniden yükle
      await fetchBackups();
    } catch (error) {
      console.error('Yedekleme hatası:', error);
      setSnackbar({
        open: true,
        message: `Artan yedek oluşturulurken bir hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`,
        severity: 'error'
      });
    } finally {
      setBackupLoading(false);
    }
  };

  // Yedeği indir
  const handleDownloadBackup = (backupId: string) => {
    try {
      const downloadUrl = databaseBackupService.getDownloadUrl(backupId);
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Yedek indirme URL\'si oluşturulurken hata oluştu:', error);
      setSnackbar({
        open: true,
        message: 'Yedek indirme URL\'si oluşturulurken hata oluştu',
        severity: 'error'
      });
    }
  };

  // Geri yükleme dialogunu açma
  const handleOpenRestoreDialog = (backup: DatabaseBackup) => {
    setSelectedBackup(backup);
    setRestoreDialogOpen(true);
  };

  // Geri yükleme işlemi
  const handleRestoreBackup = async () => {
    if (!isAuthenticated || !selectedBackup) return;
    
    try {
      setRestoreLoading(true);
      const primaryDbId = selectedDatabases[0];
      const result = await databaseBackupService.restoreBackup(primaryDbId, selectedBackup.id);
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Veritabanı başarıyla geri yüklendi',
          severity: 'success'
        });
        // Geri yükleme başarılı olduğunda dialogu kapat
        setRestoreDialogOpen(false);
        // Yedekleri tekrar yükle
        fetchBackups();
      } else {
        setSnackbar({
          open: true,
          message: `Hata: ${result.message || 'Bilinmeyen hata'}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Restore error:', error);
      setSnackbar({
        open: true,
        message: `Geri yükleme sırasında bir hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`,
        severity: 'error'
      });
    } finally {
      setRestoreLoading(false);
    }
  };
  
  // Yedekleme görevi silme dialogunu açma
  const handleOpenDeleteJobDialog = (job: BackupJob) => {
    // BackupJob'u BackupJobDto'ya dönüştür
    setCurrentJob(convertToBackupJobDto(job));
    setDeleteJobDialogOpen(true);
  };

  // Yedekleme görevi silme
  const handleDeleteJob = () => {
    if (!currentJob || !currentJob.id) return;
    
    setBackupSettings(prev => ({
      ...prev,
      backupJobs: prev.backupJobs.filter(job => String(job.id) !== String(currentJob.id))
    }));
    
    setSnackbar({
      open: true,
      message: 'Yedekleme görevi silindi',
      severity: 'success'
    });
    
    setDeleteJobDialogOpen(false);
    setCurrentJob(null);
  };

  // Snackbar kapatma
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Yedekleme ayarlarını güncelleme
  const handleBackupSettingsChange = (field: keyof DatabaseBackupSettings, value: any) => {
    setBackupSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Yedekleme ayarlarını kaydetme
  const handleSaveBackupSettings = async () => {
    try {
      setSettingsLoading(true);
      // Yedekleme ayarlarını ve görevleri backend'e kaydet
      await settingsService.updateDatabaseBackupSettings(backupSettings);
      
      // Görevlerin backend'de job olarak çalışması için tetikle
      if (backupSettings.backupJobs && backupSettings.backupJobs.length > 0) {
        try {
          // Backend'e görevleri job olarak kaydetmek için özel endpoint çağrısı
          await databaseBackupService.registerBackupJobs(backupSettings.backupJobs);
          console.log('Yedekleme görevleri backend job olarak kaydedildi');
        } catch (jobError) {
          console.error('Görevler job olarak kaydedilirken hata:', jobError);
          // Kullanıcıya hata gösterme, sadece ayarların kaydedildiğini bildir
        }
      }
      
      setSnackbar({
        open: true,
        message: 'Yedekleme ayarları ve görevleri başarıyla kaydedildi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Yedekleme ayarları kaydedilirken hata:', error);
      setSnackbar({
        open: true,
        message: 'Yedekleme ayarları kaydedilirken bir hata oluştu',
        severity: 'error'
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  // Basit bir ID üretme fonksiyonu (UUID yerine)
  const generateId = () => {
    return 'job_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };
  
  // Job yönetimi fonksiyonları
  const handleOpenJobDialog = (job?: BackupJob) => {
    if (job) {
      setIsEditingJob(true);
      
      // BackupJob'u BackupJobDto'ya dönüştür
      const jobDto = convertToBackupJobDto(job);
      setCurrentJob(jobDto);
      
      // Ensure job.databaseNames is an array before trying to access it
      const databaseIds = Array.isArray(job.databaseNames) ? job.databaseNames : [];
      
      // Veritabanı ID'lerini ayarla
      setSelectedDatabases(databaseIds);
    } else {
      // Yeni görev oluşturma
      setIsEditingJob(false);
      setCurrentJob({
        databaseId: selectedDatabases.length > 0 ? selectedDatabases[0] : '',
        backupType: 'full',
        frequency: 'daily',
        hour: 3,
        minute: 0,
        isEnabled: true
      });
    }
    
    setJobDialogOpen(true);
  };
  
  // Yedekleme görevi dialogunu kapatma
  const handleCloseJobDialog = () => {
    setJobDialogOpen(false);
    // Dialog kapandığında state'i temizle
    setCurrentJob(null);
  };

  const handleJobChange = (field: string, value: any) => {
    if (!currentJob) return;
    
    // Nested property update helper
    const updateNestedProperty = (obj: any, path: string[], value: any): any => {
      const [head, ...rest] = path;
      if (rest.length === 0) {
        return { ...obj, [head]: value };
      }
      return { ...obj, [head]: updateNestedProperty(obj[head] || {}, rest, value) };
    };
    
    if (field.includes('.')) {
      const path = field.split('.');
      setCurrentJob(prev => {
        if (!prev) return null;
        return updateNestedProperty(prev, path, value);
      });
    } else {
      setCurrentJob(prev => {
        if (!prev) return null;
        return { ...prev, [field]: value };
      });
    }
  };
  
  const handleSaveJob = () => {
    if (!currentJob) return;
    
    // Seçilen veritabanlarını güncelle
    const updatedJob = { ...currentJob };
    
    // Seçilen veritabanlarını BackupJobDto'ya aktar
    if (selectedDatabases.length > 0) {
      // İlk seçilen veritabanı ID'sini doğrula ve databaseId olarak ayarla
      const firstDbId = selectedDatabases[0];
      if (!firstDbId || firstDbId === 'undefined' || firstDbId === 'null') {
        console.error('Geçersiz veritabanı ID:', firstDbId);
        setSnackbar({
          open: true,
          message: 'Geçersiz veritabanı ID. Lütfen geçerli bir veritabanı seçin.',
          severity: 'error'
        });
        return;
      }
      
      // GUID formatındaki ID'yi doğrudan kullan, parseInt kullanma
      updatedJob.databaseId = firstDbId;
      
      // Seçilen veritabanının adını bul
      const selectedDb = databases.find(db => db.id === firstDbId);
      if (selectedDb) {
        updatedJob.databaseName = selectedDb.databaseName;
      } else {
        console.error('Seçilen veritabanı bulunamadı:', firstDbId);
        setSnackbar({
          open: true,
          message: 'Seçilen veritabanı bulunamadı. Lütfen geçerli bir veritabanı seçin.',
          severity: 'error'
        });
        return;
      }
      
      // Seçilen tüm veritabanlarının adlarını databaseNames alanına ekle
      const dbNames = selectedDatabases.map(dbId => {
        const db = databases.find(d => d.id === dbId);
        return db ? db.databaseName : '';
      }).filter(name => name !== '');
      
      // BackupJobDto'da databaseNames alanı yoksa, handleJobChange ile ekleyelim
      handleJobChange('databaseNames', dbNames);
    } else {
      // Hiç veritabanı seçilmediyse uyarı ver
      setSnackbar({
        open: true,
        message: 'Lütfen en az bir veritabanı seçin.',
        severity: 'warning'
      });
      return;
    }
    
    setBackupSettings(prev => {
      // Güncellenmiş BackupJobDto'yu BackupJob'a dönüştür
      const jobAsBackupJob = convertToBackupJob(updatedJob);
      
      // Seçilen tüm veritabanlarının adlarını databaseNames dizisine ekle
      // Bu adım önemli çünkü convertToBackupJob fonksiyonu databaseNames'i doğru şekilde dönüştüremeyebilir
      jobAsBackupJob.databaseNames = selectedDatabases.map(dbId => {
        const db = databases.find(d => d.id === dbId);
        return db ? db.databaseName : '';
      }).filter(name => name !== '');
      
      const updatedJobs = isEditingJob
        ? prev.backupJobs.map(job => String(job.id) === String(updatedJob.id) ? jobAsBackupJob : job)
        : [...prev.backupJobs, jobAsBackupJob];
      
      return {
        ...prev,
        backupJobs: updatedJobs
      };
    });
    
    // Konsola bilgi yazdır
    console.log('Kaydedilen job:', updatedJob);
    console.log('Seçili veritabanları:', selectedDatabases.map(dbId => {
      const db = databases.find(d => d.id === dbId);
      return db ? `${db.databaseName} (${db.companyName})` : dbId;
    }));
    
    setSnackbar({
      open: true,
      message: isEditingJob 
        ? `Yedekleme görevi güncellendi (${selectedDatabases.length} veritabanı)` 
        : `Yeni yedekleme görevi oluşturuldu (${selectedDatabases.length} veritabanı)`,
      severity: 'success'
    });
    
    // İşlem tamamlandığında dialog'u kapat
    handleCloseJobDialog();
  };

  // Yedekleme görevi durumunu değiştir
  const handleToggleJobStatus = (job: BackupJob) => {
    const newStatus = job.status === 'active' ? 'paused' : 'active';
    
    setBackupSettings(prev => ({
      ...prev,
      backupJobs: prev.backupJobs.map(j => 
        j.id === job.id ? { ...j, status: newStatus } : j
      )
    }));
    
    setSnackbar({
      open: true,
      message: `Yedekleme görevi ${newStatus === 'active' ? 'aktifleştirildi' : 'duraklatıldı'}`,
      severity: 'info'
    });
  };

  // Tarih formatı
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd MMMM yyyy HH:mm', { locale: tr });
    } catch (error) {
      return dateString;
    }
  };
  
  // Job için sonraki çalışma zamanını hesapla
  const calculateNextRunDate = (job: BackupJob): string => {
    if (!job.schedule) return 'Belirsiz';
    
    const now = new Date();
    const [hours, minutes] = job.schedule.time.split(':').map(Number);
    let nextRun = new Date(now);
    nextRun.setHours(hours, minutes, 0, 0);
    
    // Eğer bugünkü zaman geçmişse, sonraki zamanı hesapla
    if (nextRun < now) {
      switch (job.schedule.frequency) {
        case 'daily':
          nextRun.setDate(nextRun.getDate() + 1);
          break;
        case 'weekly':
          nextRun.setDate(nextRun.getDate() + 7);
          break;
        case 'monthly':
          nextRun.setMonth(nextRun.getMonth() + 1);
          break;
        case 'custom':
          if (job.schedule.customFrequencyHours) {
            nextRun = new Date(now.getTime() + job.schedule.customFrequencyHours * 60 * 60 * 1000);
          }
          break;
      }
    }
    
    return format(nextRun, 'dd MMMM yyyy HH:mm', { locale: tr });
  };
  
  // Job durumuna göre renk ve etiket belirle
  const getJobStatusInfo = (status?: string) => {
    switch (status) {
      case 'active':
        return { color: 'success', label: 'Aktif' };
      case 'paused':
        return { color: 'warning', label: 'Duraklatıldı' };
      case 'error':
        return { color: 'error', label: 'Hata' };
      default:
        return { color: 'default', label: 'Belirsiz' };
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Veritabanı Yedekleme
      </Typography>
      
      {/* Bildirim Aboneliği Bileşeni */}
      <Box sx={{ mb: 3 }}>
        <NotificationSubscriber />
      </Box>
      
      {/* Otomatik Yedekleme Ayarları kısmı kaldırıldı */}
      
      {/* Otomatik Yedekleme Ayarları */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Otomatik Yedekleme Ayarları
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenJobDialog()}
          >
            Yeni Görev
          </Button>
        </Box>
        
        {!backupSettings.backupJobs || backupSettings.backupJobs.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Henüz yedekleme görevi oluşturulmamış. Yeni bir görev eklemek için "Yeni Görev" butonuna tıklayın.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Görev Adı</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Yedekleme Tipi</TableCell>
                  <TableCell>Sıklık</TableCell>
                  <TableCell>Sonraki Çalışma</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(backupSettings.backupJobs || []).map((job) => {
                  const statusInfo = getJobStatusInfo(job.status);
                  return (
                    <TableRow key={job.id}>
                      <TableCell>{job.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={statusInfo.label}
                          color={statusInfo.color as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {job.backupType === 'full' ? 'Tam Yedek' : 
                         job.backupType === 'differential' ? 'Fark Yedeği' : 'Artımlı Yedek'}
                      </TableCell>
                      <TableCell>
                        {job.schedule.frequency === 'daily' ? 'Günlük' :
                         job.schedule.frequency === 'weekly' ? 'Haftalık' :
                         job.schedule.frequency === 'monthly' ? 'Aylık' :
                         job.schedule.frequency === 'custom' ? `${job.schedule.customFrequencyHours} Saatte Bir` : ''}
                      </TableCell>
                      <TableCell>
                        {job.nextRunDate || calculateNextRunDate(job)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={job.status === 'active' ? 'Duraklat' : 'Aktifleştir'}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleToggleJobStatus(job)}
                            color={job.status === 'active' ? 'warning' : 'success'}
                          >
                            {job.status === 'active' ? <PauseIcon /> : <PlayArrowIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Düzenle">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenJobDialog(job)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDeleteJobDialog(job)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* Manuel Yedekleme */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Manuel Yedekleme</Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="database-select-label">Veritabanı</InputLabel>
          <Select
            labelId="database-select-label"
            id="database-select"
            multiple
            value={selectedDatabases}
            label="Veritabanı"
            onChange={handleDatabaseChange}
            disabled={loading}
            renderValue={(selected) => {
              if (!Array.isArray(selected) || selected.length === 0) return 'Veritabanı seçin';
              return selected.map(id => {
                const db = databases.find(d => d.id === id);
                return db ? `${db.databaseName} (${db.companyName})` : id;
              }).join(', ');
            }}
          >
            {databases.map((db) => (
              <MenuItem key={db.id} value={db.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Checkbox 
                    checked={selectedDatabases.indexOf(db.id) > -1} 
                  />
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1">{db.databaseName}</Typography>
                    <Typography variant="caption" color="text.secondary">{db.companyName}</Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BackupIcon />}
            onClick={handleCreateFullBackup}
            disabled={selectedDatabases.length === 0 || backupLoading}
          >
            Tam Yedek Oluştur
          </Button>
          <Button
            variant="outlined"
            startIcon={<UpdateIcon />}
            onClick={handleCreateDifferentialBackup}
            disabled={selectedDatabases.length === 0 || backupLoading}
          >
            Fark Yedeği Oluştur
          </Button>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Mevcut Yedekler</Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : backups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Bu veritabanı için henüz yedek bulunmuyor.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Yedek Tipi</TableCell>
                  <TableCell>Oluşturulma Tarihi</TableCell>
                  <TableCell>Boyut</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((backup) => (
                  <TableRow key={backup.id}>
                    <TableCell>
                      <Chip 
                        label={backup.backupType === 'Full' ? 'Tam Yedek' : 'Fark Yedeği'}
                        color={backup.backupType === 'Full' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(backup.createdAt)}
                    </TableCell>
                    <TableCell>
                      {backup.fileSizeInMB.toFixed(2)} MB
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadBackup(backup.id)}
                        sx={{ mr: 1 }}
                      >
                        İndir
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => handleOpenRestoreDialog(backup)}
                        disabled={restoreLoading}
                      >
                        Geri Yükle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Geri yükleme onay dialogu */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
      >
        <DialogTitle>Veritabanı Geri Yükleme</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{selectedBackup?.backupFileName}</strong> yedeğini geri yüklemek istediğinizden emin misiniz?
            Bu işlem mevcut veritabanının üzerine yazacak ve geri alınamayacaktır.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>İptal</Button>
          <Button onClick={handleRestoreBackup} color="warning" variant="contained">
            Geri Yükle
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Yedekleme görevi ekleme/düzenleme dialogu */}
      <Dialog
        open={jobDialogOpen}
        onClose={handleCloseJobDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isEditingJob ? 'Yedekleme Görevini Düzenle' : 'Yeni Yedekleme Görevi'}</DialogTitle>
        <DialogContent>
          {/* currentJob'u BackupJob tipine dönüştür */}
          {currentJob && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Görev Adı"
                fullWidth
                value={convertToBackupJob(currentJob).name || ''}
                onChange={(e) => handleJobChange('name', e.target.value)}
                required
              />
              
              <FormControl fullWidth>
                <InputLabel>Veritabanları</InputLabel>
                <Select
                  multiple
                  value={selectedDatabases}
                  onChange={(e) => {
                    const newSelectedDatabases = e.target.value as string[];
                    setSelectedDatabases(newSelectedDatabases);
                    
                    // Aynı zamanda currentJob'un databaseNames alanını da güncelle
                    const dbNames = newSelectedDatabases.map(dbId => {
                      const db = databases.find(d => d.id === dbId);
                      return db ? db.databaseName : '';
                    }).filter(name => name !== '');
                    
                    handleJobChange('databaseNames', dbNames);
                  }}
                  renderValue={(selected) => {
                    if (!Array.isArray(selected) || selected.length === 0) return 'Seçilen veritabanı yok';
                    return selected.map(dbId => {
                      const db = databases.find(d => d.id === dbId);
                      return db ? `${db.databaseName} (${db.companyName})` : dbId;
                    }).join(', ');
                  }}
                >
                {databases.map((db) => (
                  <MenuItem key={db.id} value={db.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Checkbox 
                        checked={selectedDatabases.indexOf(db.id) > -1} 
                      />
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body1">{db.databaseName}</Typography>
                        <Typography variant="caption" color="text.secondary">{db.companyName}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Yedekleme Tipi</InputLabel>
              <Select
                value={convertToBackupJob(currentJob).backupType || 'full'}
                onChange={(e) => handleJobChange('backupType', e.target.value)}
              >
                <MenuItem value="full">Tam Yedekleme</MenuItem>
                <MenuItem value="differential">Fark Yedeği</MenuItem>
                <MenuItem value="incremental">Artımlı Yedekleme</MenuItem>
              </Select>
            </FormControl>
            
            <Typography variant="subtitle1" sx={{ mt: 1 }}>Zamanlama</Typography>
            
            <FormControl fullWidth>
              <InputLabel>Sıklık</InputLabel>
              <Select
                value={convertToBackupJob(currentJob).schedule?.frequency || 'daily'}
                onChange={(e) => handleJobChange('schedule.frequency', e.target.value)}
              >
                <MenuItem value="daily">Günlük</MenuItem>
                <MenuItem value="weekly">Haftalık</MenuItem>
                <MenuItem value="monthly">Aylık</MenuItem>
                <MenuItem value="custom">Özel</MenuItem>
              </Select>
            </FormControl>
            
            {convertToBackupJob(currentJob).schedule?.frequency === 'custom' && (
              <TextField
                label="Özel Sıklık (Saat)"
                type="number"
                fullWidth
                value={convertToBackupJob(currentJob).schedule?.customFrequencyHours || 24}
                onChange={(e) => handleJobChange('schedule.customFrequencyHours', parseInt(e.target.value))}
                InputProps={{ inputProps: { min: 1, max: 720 } }}
              />
            )}
            
            <TextField
              label="Çalışma Saati"
              type="time"
              fullWidth
              value={convertToBackupJob(currentJob).schedule?.time || '03:00'}
              onChange={(e) => handleJobChange('schedule.time', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            
            <Typography variant="subtitle1" sx={{ mt: 1 }}>Saklama Politikası</Typography>
            
            <TextField
              label="Saklama Süresi (Gün)"
              type="number"
              fullWidth
              value={convertToBackupJob(currentJob).retention?.keepBackupsForDays || 30}
              onChange={(e) => handleJobChange('retention.keepBackupsForDays', parseInt(e.target.value))}
              InputProps={{ inputProps: { min: 1, max: 365 } }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={convertToBackupJob(currentJob).retention?.compressBackups || false}
                  onChange={(e) => handleJobChange('retention.compressBackups', e.target.checked)}
                />
              }
              label="Sıkıştırma Kullan"
            />
            
            <Typography variant="subtitle1" sx={{ mt: 1 }}>Hedef</Typography>
            
            <TextField
              label="Yedekleme Dizini"
              fullWidth
              value={convertToBackupJob(currentJob).destination?.directory || '/backups'}
              onChange={(e) => handleJobChange('destination.directory', e.target.value)}
            />
          </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseJobDialog}>İptal</Button>
          <Button 
            onClick={handleSaveJob} 
            variant="contained" 
            color="primary"
            disabled={!currentJob || !convertToBackupJob(currentJob).name || (convertToBackupJob(currentJob).databaseNames?.length || 0) === 0}
          >
            {isEditingJob ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Yedekleme görevi silme dialogu */}
      <Dialog
        open={deleteJobDialogOpen}
        onClose={() => setDeleteJobDialogOpen(false)}
      >
        <DialogTitle>Yedekleme Görevini Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{currentJob ? convertToBackupJob(currentJob).name : ''}</strong> isimli yedekleme görevini silmek istediğinizden emin misiniz?
            Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteJobDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteJob} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bildirim snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Yükleme göstergesi */}
      {backupLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>İşlem devam ediyor, lütfen bekleyin...</Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default DatabaseBackupTab;
